import { defaultState } from '../domain/defaults';
import type { StoredTreasuryState } from '../domain/types';

const STORAGE_KEY = 'sgrbc-treasury-state-v1';

export interface TreasuryRepository {
  load(): StoredTreasuryState;
  save(state: StoredTreasuryState): void;
  clear(): void;
}

export class LocalStorageTreasuryRepository implements TreasuryRepository {
  load(): StoredTreasuryState {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return structuredClone(defaultState);

    try {
      return normalizeState(JSON.parse(raw));
    } catch {
      return structuredClone(defaultState);
    }
  }

  save(state: StoredTreasuryState) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  clear() {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export function normalizeState(input: unknown): StoredTreasuryState {
  const candidate = input as Partial<StoredTreasuryState>;
  if (isLegacySampleState(candidate)) return structuredClone(defaultState);

  return {
    settings: {
      ...defaultState.settings,
      ...(candidate.settings ?? {}),
      firstReportMonth:
        typeof candidate.settings?.firstReportMonth === 'string'
          ? candidate.settings.firstReportMonth
          : defaultState.settings.firstReportMonth,
      openingBalances: {
        ...defaultState.settings.openingBalances,
        ...(candidate.settings?.openingBalances ?? {})
      },
      openingBalancesConfirmed: Boolean(candidate.settings?.openingBalancesConfirmed),
      reportDisplay: {
        ...defaultState.settings.reportDisplay,
        ...(candidate.settings?.reportDisplay ?? {})
      },
      categories: Array.isArray(candidate.settings?.categories)
        ? candidate.settings.categories
        : defaultState.settings.categories
    },
    transactions: Array.isArray(candidate.transactions) ? candidate.transactions : defaultState.transactions,
    reportNotes: candidate.reportNotes && typeof candidate.reportNotes === 'object' ? candidate.reportNotes : defaultState.reportNotes
  };
}

function isLegacySampleState(candidate: Partial<StoredTreasuryState>) {
  const transactions = candidate.transactions;
  if (!Array.isArray(transactions) || transactions.length === 0) return false;
  return transactions.every((transaction) => typeof transaction?.id === 'string' && transaction.id.startsWith('seed-'));
}
